import React from 'react';
import { useGenerateVizPanelDefinition } from '@/components/VizContainer/hooks';
import { ContentDimensions, JSONValue, PanelDefinition, UnknownSpec } from '../../../core';
import { BasePanelProps, PanelChromeProps, VizComponentProps } from '../../model';
import { CommonChartProps } from '../../utils';
import { ResponsivePanelWrapper } from '../ResponsivePanelWrapper';
import { VizPanel } from '../VizPanel';

export interface VizPanelWrapperProps<ComponentProps extends BasePanelProps, DashboardSpec extends JSONValue>
  extends Partial<ContentDimensions> {
  chartKind?: string;
  chartProps: VizComponentProps<ComponentProps>;
  children: React.ReactElement;
  mapPropsToSpec?: (props: Omit<ComponentProps, CommonChartProps>) => DashboardSpec;
  panelChromeProps?: PanelChromeProps;
}

const EMPTY_PANEL_CHROME_PROPS: PanelChromeProps = {};

/**
 * Wrapper component used for context-aware data visualizations use cases when they are wrapped by a `VizPanel`.
 */
export const VizPanelWrapper = <ComponentProps extends BasePanelProps, DashboardSpec extends JSONValue>({
  chartKind,
  chartProps,
  children,
  width,
  height,
  mapPropsToSpec,
  panelChromeProps = EMPTY_PANEL_CHROME_PROPS,
}: VizPanelWrapperProps<ComponentProps, DashboardSpec>) => {
  const { panelDefinition } = useGenerateVizPanelDefinition({
    chartKind,
    chartProps,
    mapPropsToSpec,
    panelChromeProps,
  });

  const { panelRef, ...otherPanelChromeProps } = panelChromeProps;

  return (
    <ResponsivePanelWrapper width={width} height={height}>
      {({ width, height }) => (
        <VizPanel
          ref={panelRef}
          panelKey={chartProps.panelKey}
          {...otherPanelChromeProps}
          panelDefinition={panelDefinition as PanelDefinition<UnknownSpec>}
          width={width}
          height={height}
        >
          {children}
        </VizPanel>
      )}
    </ResponsivePanelWrapper>
  );
};
